import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./MyAssetsCards.module.css";

const getUserBalanceEndpoint =
  "https://distribution-calculator-256611876551.europe-west1.run.app/get_user_balance_solana";

const safeParse = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Groups distribution rows by month and builds table rows: Period, Interest, APY, Balance After.
 * Uses same API as CumulativeInterest: get_user_balance_solana.
 */
function InterestEarnedCard({ walletAddress, currentApy, maxRows = 5 }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!walletAddress) {
      setRows([]);
      setLoading(false);
      return;
    }
    const fetchData = async () => {
      try {
        const res = await fetch(`${getUserBalanceEndpoint}?user_address=${walletAddress}`);
        const json = await res.json();
        const data = json?.data || [];
        const withHash = data
          .filter(
            (r) =>
              r.distribution_transaction_hash &&
              r.distribution_transaction_hash !== "0" &&
              r.distribution_transaction_hash.toLowerCase() !== "none"
          )
          .sort((a, b) => Number(b.block_number) - Number(a.block_number));

        const byPeriod = {};
        withHash.forEach((r) => {
          let periodKey = "";
          if (r.distribution_time && r.distribution_time !== "" && r.distribution_time.toLowerCase() !== "none") {
            const d = new Date(r.distribution_time);
            periodKey = d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
          } else {
            periodKey = "—";
          }
          if (!byPeriod[periodKey]) {
            byPeriod[periodKey] = {
              interest: 0,
              endBalance: undefined,
            };
          }
          const amount = safeParse(r.undistributed_usdi);
          const balance = safeParse(r.balance);
          byPeriod[periodKey].interest += amount;
          // withHash is sorted newest-first, so preserve the first (latest) balance we see
          if (byPeriod[periodKey].endBalance === undefined && Number.isFinite(balance)) {
            byPeriod[periodKey].endBalance = balance;
          }
        });

        const tableRows = Object.entries(byPeriod)
          .map(([period, v]) => {
            const interest = v.interest;
            const endBalance = v.endBalance != null ? v.endBalance : 0;
            const startBalance = Math.max(endBalance - interest, 0);

            let apy = null;
            if (startBalance > 0 && interest > 0) {
              const rPeriod = interest / startBalance; // simple period return
              // Treat grouped period as roughly one month and annualize
              const annualized = Math.pow(1 + rPeriod, 12) - 1;
              apy = annualized * 100;
            }

            return {
              period,
              interest,
              balanceAfter: endBalance,
              apy,
            };
          })
          .filter((r) => r.period !== "—")
          .sort((a, b) => {
            const da = new Date(a.period);
            const db = new Date(b.period);
            return db - da;
          })
          .slice(0, maxRows);

        setRows(tableRows);
      } catch (e) {
        console.error(e);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [walletAddress, currentApy, maxRows]);

  return (
    <div className={`${styles.card} ${styles.tableCard}`}>
      <div className={styles.tableCardHeader}>
        <h3 className={styles.tableCardTitle}>Interest Earned</h3>
        <button
          type="button"
          className={styles.viewAllLink}
          onClick={() => navigate("/transaction")}
        >
          View All
        </button>
      </div>
      <table className={styles.dataTable}>
        <thead>
          <tr>
            <th>Period</th>
            <th>Interest</th>
            <th>APY</th>
            <th>Balance After</th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td colSpan={4} className={styles.loadingRow}>
                Loading…
              </td>
            </tr>
          )}
          {!loading && rows.length === 0 && (
            <tr>
              <td colSpan={4} className={styles.loadingRow}>
                No data
              </td>
            </tr>
          )}
          {!loading &&
            rows.map((row, i) => (
              <tr key={`${row.period}-${i}`}>
                <td>{row.period}</td>
                <td className={styles.interestPositive}>
                  +{row.interest.toLocaleString("en-US", { maximumFractionDigits: 2 })} USDi
                </td>
                <td>{row.apy != null ? `${Number(row.apy).toFixed(2)}%` : "—"}</td>
                <td>
                  {row.balanceAfter.toLocaleString("en-US", { maximumFractionDigits: 2 })} USDi
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

export default InterestEarnedCard;
